import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center" data-testid="tos-logo-link">
              <img src="https://customer-assets.emergentagent.com/job_38ea2e2e-0b2a-4c96-90e7-893185248e11/artifacts/fcaqs56k_logo.png" alt="Guardifix" className="h-8" />
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2" data-testid="tos-back-btn">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 pt-28 pb-16">
        <div className="space-y-8">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Terms of Service</h1>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Acceptance of Terms</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                By accessing or using Guardifix's encrypted email platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree, please discontinue use immediately. These terms form a binding legal agreement between you and Guardifix.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Service Description</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Guardifix delivers encrypted email communication, secure file storage, and related collaboration tools built on zero-knowledge architecture. The platform is provided on an "as is" basis, and we reserve the right to modify, suspend, or retire any feature at any time with reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Account Registration</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">To create an account, you must:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Be at least 16 years of age</li>
                <li>Provide accurate, complete registration details</li>
                <li>Maintain the confidentiality of your credentials</li>
                <li>Immediately report any unauthorized access</li>
                <li>Accept responsibility for all activity under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Acceptable Use</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">You agree not to use Guardifix to:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Violate any applicable law or regulation</li>
                <li>Transmit spam, bulk marketing, or unsolicited messages</li>
                <li>Distribute malware, ransomware, or other harmful code</li>
                <li>Harass, threaten, defame, or discriminate against others</li>
                <li>Infringe intellectual property or proprietary rights</li>
                <li>Attempt unauthorized access to Guardifix systems or networks</li>
                <li>Disrupt service availability for other users</li>
                <li>Engage in any illegal or fraudulent activity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Privacy and Data Protection</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                Guardifix uses zero-knowledge encryption — we structurally cannot read the content of your encrypted messages. We collect limited operational metadata as described in our Privacy Policy. Your continued use signifies acceptance of those data practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Payment Terms</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">For paid plans:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>Subscription fees are billed in advance for each billing cycle</li>
                <li>Fees are non-refundable unless required by applicable law</li>
                <li>We may adjust pricing with 30 days written notice</li>
                <li>Subscriptions auto-renew unless cancelled before the renewal date</li>
                <li>Non-payment may result in account suspension or termination</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Intellectual Property</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                All content, software, graphics, logos, trademarks, and design elements within Guardifix are the exclusive property of Guardifix and are protected under international intellectual property laws. No license or right is granted to reproduce, distribute, or create derivative works without prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                To the fullest extent permitted by law, Guardifix shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising from your use of the service, regardless of whether such damages were foreseeable.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Warranty Disclaimer</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                The platform is provided "as is" and "as available" without any warranty, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement. Guardifix does not guarantee uninterrupted or error-free operation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Account Termination</h2>
              <p className="text-slate-600 leading-relaxed text-sm mb-3">We may suspend or terminate your account if:</p>
              <ul className="list-disc pl-6 space-y-1.5 text-slate-600 text-sm">
                <li>You breach these Terms of Service</li>
                <li>You engage in fraudulent or unlawful conduct</li>
                <li>Your account poses a security risk to the platform</li>
                <li>Required by valid legal process or court order</li>
              </ul>
              <p className="text-slate-600 leading-relaxed text-sm mt-3">
                You may close your account at any time through account settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Indemnification</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                You agree to indemnify, defend, and hold harmless Guardifix, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your use of the platform or breach of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>12. Governing Law</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                These Terms shall be governed by and construed in accordance with the laws of Switzerland, without regard to conflict-of-law principles. Disputes shall be resolved exclusively in the courts of Switzerland.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>13. Modifications</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                We reserve the right to update these Terms at any time. Material changes will be posted on this page with an updated revision indicator. Your continued use of the platform following posted changes constitutes your acceptance of the revised terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>14. Severability</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                If any provision of these Terms is held unenforceable or invalid by a court of competent jurisdiction, that provision will be modified to the minimum extent necessary, and the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>15. Contact</h2>
              <p className="text-slate-600 leading-relaxed text-sm">
                For questions about these Terms of Service, contact us at:
              </p>
              <p className="text-slate-600 text-sm mt-3">
                <strong>Email:</strong> info@guardifix.com
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
